import {
    defaults,
    defaultTo,
    endsWith,
    flow,
    filter,
    getOr,
    groupBy,
    isEmpty,
    map,
    mapValues,
    negate,
    noop,
    prop,
    sortBy,
} from 'lodash/fp';

import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { IntlProvider } from 'react-intl';

import '../style/css/rio-appnavigator.less';

import AppNavigatorItem from './../components/AppNavigatorItem';
import TabbedAppNavigator from './../components/TabbedAppNavigator';
import { configureFetchApps, getBucketFileUrl } from '../fetchApps';

import {
    DEFAULT_LOCALE,
    getSupportedLocale,
    getLanguageData,
} from '../lang';

const isAdministrative = getOr(false, 'meta.administration');

const getMessagesByLocale = (locale, app) => flow(
    prop(`meta.translation.${locale}`),
    defaultTo(prop(`meta.translation.${DEFAULT_LOCALE}`, app)),
    defaultTo(prop('meta.translation.en-US', app))
)(app);

const extractIconUrl = app => {
    const iconUrl = app.iconUrl;

    if (!iconUrl) {
        // Fallback to fixed icon url
        return getBucketFileUrl('tileIcon2.svg', app);
    }

    if (/^https?:\/\//.test(iconUrl)) {
        // Absolute paths are taken as is
        return iconUrl;
    }

    // Relative paths will be resolved within the bucket
    return getBucketFileUrl(iconUrl, app);
};

export const configureDefaultAppNavigator = (fetch, location) => {
    const { fetchApps } = configureFetchApps(fetch, location);

    const isOnline = /\.rio\.cloud$/.test(location.host);
    const isProd = /start\.rio\.cloud$/.test(location.host);
    const isStaging = /test\.rio\.cloud$/.test(location.host);

    const extractRoute = (getBaseUrl, app) => {
        const { route } = app;
        const routeIsAbsolute = /^https?:\/\//.test(route);
        const isOwnAccount = isOnline && (!isProd && !isStaging);

        if (routeIsAbsolute) {
            if (isStaging) {
                // Rewriting paths to stay in staging
                return route.replace(
                    /^:\/\/start\.rio\.cloud/,
                    '://test.rio.cloud'
                );
            }

            // Otherwise, absolute paths are taken as is
            return route;
        }

        const relativeRoute = route;

        if (isOwnAccount) {
            // Relative paths will be resolved within the current PROD url
            return `https://start.rio.cloud${relativeRoute}`;
        }

        // All other cases should be fine with relative urls
        // and the potentially user defined logic for determining
        // the base url
        return `${getBaseUrl(relativeRoute)}${relativeRoute}`;
    };

    return class DefaultAppNavigator extends Component {

        constructor(props) {
            super(props);

            this.createNavigationItem = this.createNavigationItem.bind(this);
            this.fetchApps = this.fetchApps.bind(this);

            this.state = {
                apps: null,
                languageData: null,
                locale: DEFAULT_LOCALE,
            };
        }

        createNavigationItem(app, key) {
            const { getBaseUrlForRelativeRoute } = this.props;
            const { locale } = this.state;

            if (!app.meta) {
                // eslint-disable-next-line no-console
                console.warn('Encountered App without "meta", no navigation item will be created', app);
                return null;
            }

            const tileMessagesByLocale = getMessagesByLocale(locale, app);

            const nameKey = tileMessagesByLocale.name;
            const iconUrl = extractIconUrl(app);
            const route = extractRoute(getBaseUrlForRelativeRoute, app);

            return {
                sortKey: nameKey,
                route: route,
                navElement:
                    <AppNavigatorItem
                        name={nameKey}
                        tileIcon={iconUrl}
                        handleOnClick={noop}
                        route={route}
                        key={key}
                        group={app.meta.group}
                    />,
            };
        }

        createMenuEntries(apps) {
            const processAppGroup = flow(
                map(this.createNavigationItem),
                filter(negate(isEmpty)),
                sortBy('sortKey'),
                map('navElement')
            );
            const myDefaults = { administrative: [], normal: [], heroes: [] };

            if (!apps) {
                return mapValues(processAppGroup, myDefaults);
            }

            const contentTabs = flow(
                groupBy(app => {
                    if (endsWith('/marketplace', app.route)) {
                        return 'heroes';
                    }

                    return isAdministrative(app) ? 'administrative' : 'normal';
                }),
                defaults(myDefaults),
                mapValues(processAppGroup)
            )(apps);

            return contentTabs;
        }

        render() {
            const { apps, languageData, locale } = this.state;
            const items = this.createMenuEntries(apps);

            return (
                <IntlProvider defaultLocale={DEFAULT_LOCALE} key={locale} locale={locale} messages={languageData}>
                    <TabbedAppNavigator>
                        {items}
                    </TabbedAppNavigator>
                </IntlProvider>
            );
        }

        fetchApps() {
            const {
                accessToken,
                appsEndpoint,
                fallbackLocation,
            } = this.props;

            fetchApps(appsEndpoint, accessToken, fallbackLocation)
                .then(apps => this.setState({ apps }))
                // eslint-disable-next-line no-console
                .catch(console.error);
        }

        componentWillReceiveProps() {
            //console.log('componentWillReceiveProps', nextProps);
            this.fetchApps();
        }

        componentDidMount() {
            const locale = getSupportedLocale(this.props.locale);

            this.setState({
                languageData: getLanguageData(locale),
                locale,
            });

            this.fetchApps();
        }
    };
};

const DefaultAppNavigator = configureDefaultAppNavigator(
    window.fetch,
    window.location
);

// FIXME: For now the `Default` refers to the de-facto default dictated
//        by the status quo. I suppose  for MultiAccounts we should provide
//        another variant
export default DefaultAppNavigator;

const getBaseUrlForRelativeRoute = () => 'https://start.rio.cloud';

DefaultAppNavigator.defaultProps = {
    // FIXME : I'm not really sure that this should be the default
    //         but people seem to assume it is, so there's that
    appsEndpoint: 'https://api.iam.ccp-prod.net/appregistry/apps',
    className: '',
    getBaseUrlForRelativeRoute,
};

DefaultAppNavigator.propTypes = {
    accessToken: PropTypes.string.isRequired,
    appsEndpoint: PropTypes.string,
    className: PropTypes.string,
    fallbackLocation: PropTypes.string,
    // FIXME: This is basically just here to support the backport
    //        of the SmartComponents into `rio-applicationframe` when
    //        in development.
    //        It is being used to modify the relative route behavior
    //        depending on the context the component is used in:
    //        * In the monolith we want the relative URLs to remain
    //          relative so you can continue to use the workflow
    //          you're used to
    //        * When developing your own SPA the relative URLs should
    //          be turned into calls to PROD
    getBaseUrlForRelativeRoute: PropTypes.func,
    locale: PropTypes.string.isRequired,
};
